import React, { useState, useEffect, useRef } from 'react';

interface LogEntry {
  text: string;
  timestamp: string | null;
}

interface SpeechSDKLogEntry {
  timestamp: string;
  level: string;
  conversation_id: string;
  event_type: string;
  sdk_data: any;
  error?: any;
}

interface LogsResponse {
  total_lines: number;
  filtered_lines: number;
  returned_lines: number;
  logs: LogEntry[];
  log_file_path: string;
  error?: string;
}

interface SpeechSDKLogsResponse {
  logs: SpeechSDKLogEntry[];
  stats: {
    total_logs: number;
    max_capacity: number;
    oldest_log: string | null;
    newest_log: string | null;
  };
  total_returned: number;
  timestamp: string;
}

// Get backend URL from environment - use backend directly, not frontend proxy
const getBackendUrl = (): string => {
  return process.env.REACT_APP_API_BASE_URL || 'https://learning-companion-be-dev.data-nonprod.edgenuity.com';
};

const BackendLogsViewer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'backend' | 'speech-sdk'>('backend');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [speechSDKLogs, setSpeechSDKLogs] = useState<SpeechSDKLogEntry[]>([]);
  const [speechSDKStats, setSpeechSDKStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalLines, setTotalLines] = useState(0);
  const [filteredLines, setFilteredLines] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [logLevel, setLogLevel] = useState<string>('');
  const [lineCount, setLineCount] = useState(100);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        lines: lineCount.toString(),
      });
      
      if (searchText) params.append('search', searchText);
      if (logLevel) params.append('level', logLevel);
      
      // Use backend URL directly instead of frontend proxy
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/database/logs?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: LogsResponse = await response.json();
      
      if (data.error) {
        setError(data.error);
        setLogs([]);
      } else {
        setLogs(data.logs);
        setTotalLines(data.total_lines);
        setFilteredLines(data.filtered_lines);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSpeechSDKLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/v1/logs/speech-sdk?limit=${lineCount}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: SpeechSDKLogsResponse = await response.json();
      setSpeechSDKLogs(data.logs);
      setSpeechSDKStats(data.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch Speech SDK logs');
      setSpeechSDKLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const clearSpeechSDKLogs = async () => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/v1/logs/speech-sdk`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      await fetchSpeechSDKLogs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear Speech SDK logs');
    }
  };

  const downloadLogs = async () => {
    try {
      // Use backend URL directly instead of frontend proxy
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/database/logs/download`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backend-logs-${new Date().toISOString().slice(0, 10)}.log`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download logs');
    }
  };

  const downloadSpeechSDKLogs = async () => {
    try {
      // Format Speech SDK logs as text
      const logsText = speechSDKLogs.map(log => {
        const separator = '='.repeat(80);
        const lines = [
          `=${separator}`,
          `Timestamp: ${new Date(log.timestamp).toLocaleString()}`,
          `Level: ${log.level}`,
          `Event Type: ${log.event_type}`,
          `Conversation ID: ${log.conversation_id}`,
          ``,
          `SDK Data:`,
          JSON.stringify(log.sdk_data, null, 2)
        ];
        
        if (log.error) {
          lines.push('', 'Error Details:');
          lines.push(JSON.stringify(log.error, null, 2));
        }
        
        lines.push(`=${separator}`, '');
        return lines.join('\n');
      }).join('\n');
      
      const headerSeparator = '='.repeat(80);
      const header = `Speech SDK Diagnostic Logs
Generated: ${new Date().toLocaleString()}
Total Logs: ${speechSDKLogs.length}
${headerSeparator}

`;
      
      const fullText = header + logsText;
      const blob = new Blob([fullText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `speech-sdk-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download Speech SDK logs');
    }
  };

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (activeTab === 'backend') {
      fetchLogs();
    } else {
      fetchSpeechSDKLogs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        if (activeTab === 'backend') {
          fetchLogs();
        } else {
          fetchSpeechSDKLogs();
        }
      }, 5000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, searchText, logLevel, lineCount]);

  const getLogLevelColor = (text: string): string => {
    if (text.includes('ERROR')) return 'text-red-600';
    if (text.includes('WARNING')) return 'text-yellow-600';
    if (text.includes('INFO')) return 'text-blue-600';
    if (text.includes('DEBUG')) return 'text-gray-600';
    return 'text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-4">
        <h3 className="text-xl font-semibold mb-4">Backend Logs</h3>
        
        {/* Tab Navigation */}
        <div className="flex space-x-2 mb-4 border-b">
          <button
            onClick={() => setActiveTab('backend')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'backend'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            📄 Backend Logs
          </button>
          <button
            onClick={() => setActiveTab('speech-sdk')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'speech-sdk'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            🎤 Speech SDK Logs
            {speechSDKStats && (
              <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                {speechSDKStats.total_logs}
              </span>
            )}
          </button>
        </div>
        
        {/* Controls - Backend Logs */}
        {activeTab === 'backend' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Filter logs..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Log Level
                </label>
                <select
                  value={logLevel}
                  onChange={(e) => setLogLevel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Levels</option>
                  <option value="DEBUG">DEBUG</option>
                  <option value="INFO">INFO</option>
                  <option value="WARNING">WARNING</option>
                  <option value="ERROR">ERROR</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lines
                </label>
                <select
                  value={lineCount}
                  onChange={(e) => setLineCount(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="50">50 lines</option>
                  <option value="100">100 lines</option>
                  <option value="500">500 lines</option>
                  <option value="1000">1000 lines</option>
                  <option value="5000">5000 lines</option>
                </select>
              </div>
              
              <div className="flex items-end gap-2">
                <button
                  onClick={fetchLogs}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={downloadLogs}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                📥 Download Logs
              </button>
              
              <button
                onClick={scrollToBottom}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                ⬇️ Scroll to Bottom
              </button>
              
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-4 py-2 rounded-md ${
                  autoRefresh
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-gray-600 hover:bg-gray-700'
                } text-white`}
              >
                {autoRefresh ? '⏸️ Stop Auto-refresh' : '▶️ Auto-refresh (5s)'}
              </button>
            </div>
            
            {/* Stats */}
            <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
              <span>Total: {totalLines.toLocaleString()} lines</span>
              {searchText || logLevel ? (
                <span>Filtered: {filteredLines.toLocaleString()} lines</span>
              ) : null}
              <span>Showing: {logs.length} lines</span>
            </div>
          </>
        )}

        {/* Controls - Speech SDK Logs */}
        {activeTab === 'speech-sdk' && (
          <>
            {/* Stats */}
            {speechSDKStats && (
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Total Logs:</span>
                    <span className="ml-2 font-semibold text-blue-800">
                      {speechSDKStats.total_logs} / {speechSDKStats.max_capacity || 500}
                    </span>
                  </div>
                  {speechSDKStats.oldest_log && (
                    <div>
                      <span className="text-gray-600">Oldest:</span>
                      <span className="ml-2 font-medium text-gray-800">
                        {new Date(speechSDKStats.oldest_log).toLocaleTimeString()}
                      </span>
                    </div>
                  )}
                  {speechSDKStats.newest_log && (
                    <div>
                      <span className="text-gray-600">Newest:</span>
                      <span className="ml-2 font-medium text-gray-800">
                        {new Date(speechSDKStats.newest_log).toLocaleTimeString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={fetchSpeechSDKLogs}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : '🔄 Refresh'}
              </button>
              
              <button
                onClick={downloadSpeechSDKLogs}
                disabled={speechSDKLogs.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                📥 Download Logs
              </button>
              
              <button
                onClick={clearSpeechSDKLogs}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                🗑️ Clear Logs
              </button>
              
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-4 py-2 rounded-md ${
                  autoRefresh
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-gray-600 hover:bg-gray-700'
                } text-white`}
              >
                {autoRefresh ? '⏸️ Stop Auto-refresh' : '▶️ Auto-refresh (5s)'}
              </button>
            </div>
          </>
        )}
      </div>
      
      {/* Error display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800 font-medium">Error: {error}</p>
        </div>
      )}
      
      {/* Backend Logs display */}
      {activeTab === 'backend' && (
        <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm overflow-auto max-h-[600px]">
          {logs.length === 0 ? (
            <p className="text-gray-400">No logs available</p>
          ) : (
            <div className="space-y-1">
              {logs.map((log, index) => (
                <div
                  key={index}
                  className={`${getLogLevelColor(log.text)} hover:bg-gray-800 px-2 py-1 rounded`}
                >
                  <span className="text-gray-300">{log.text}</span>
                </div>
              ))}
              <div ref={logsEndRef} />
            </div>
          )}
        </div>
      )}

      {/* Speech SDK Logs display */}
      {activeTab === 'speech-sdk' && (
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {speechSDKLogs.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
              No Speech SDK logs available
            </div>
          ) : (
            speechSDKLogs.map((log, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                {/* Log Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      log.level === 'ERROR' ? 'bg-red-100 text-red-800' :
                      log.level === 'WARNING' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {log.level}
                    </span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                      {log.event_type}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(log.timestamp).toLocaleString()}
                  </span>
                </div>

                {/* Conversation ID */}
                {log.conversation_id && (
                  <div className="mb-2 text-sm">
                    <span className="text-gray-600 font-medium">Conversation ID:</span>{' '}
                    <span className="text-gray-800 font-mono text-xs">{log.conversation_id}</span>
                  </div>
                )}

                {/* SDK Data */}
                {log.sdk_data && Object.keys(log.sdk_data).length > 0 && (
                  <div className="mb-2">
                    <div className="text-sm font-medium text-gray-700 mb-1">SDK Data:</div>
                    <div className="bg-gray-50 rounded p-2 text-xs font-mono overflow-x-auto">
                      <pre className="whitespace-pre-wrap">{JSON.stringify(log.sdk_data, null, 2)}</pre>
                    </div>
                  </div>
                )}

                {/* Error Data */}
                {log.error && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded p-3">
                    <div className="text-sm font-medium text-red-800 mb-2">Error Details:</div>
                    {log.error.message && (
                      <div className="mb-2">
                        <span className="text-xs font-semibold text-red-700">Message:</span>
                        <div className="text-xs text-red-900 mt-1">{log.error.message}</div>
                      </div>
                    )}
                    {log.error.traceback && (
                      <div>
                        <span className="text-xs font-semibold text-red-700">Traceback:</span>
                        <div className="bg-red-100 rounded p-2 mt-1 text-xs font-mono overflow-x-auto">
                          <pre className="whitespace-pre-wrap text-red-900">{log.error.traceback}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default BackendLogsViewer;
